import { css } from '@emotion/css'

import Clickable from '@v2/components/common/presentational/clickables/default';
import { packAndTrack } from '@v2/services/analytics.service';
import colors from '@styles/colors';
import { mobileOnly, tabletLandscapeUp } from "@v2/styles/breakpoints";
import { useQuizModal, useBuildBundleModal } from '@components/common/presentational/modal/common';
import { useIsSwapping } from '@components/common/products/utils';
import { useHistoryWithReferrer } from '@v2/utils/history';

import imageMethodQuiz from '@img/box-builder/method-quiz.png';
import imageMethodBox from '@img/box-builder/method-box.png';


const style = css({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    flexDirection: 'column',
    alignContent: 'center',
    flexGrow: 1,
    marginBottom: 57,
    [mobileOnly]: {
        marginBottom: 40,
    },

    [tabletLandscapeUp]: {
        flexDirection: 'row',
    },

    '.method-title': {
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 20,
        lineHeight: '135%',
        textAlign: 'center',
        letterSpacing: '0.02em',
        marginBottom: 5,
        color: colors.NAVY,
    },

    '.method-image': {
        textAlign: 'center',
        padding: '0 15px 0 5px',
        width: '100%',

        img: {
            mixBlendMode: 'multiply',
            maxWidth: '100%',
            height: 'auto',
            margin: '0 auto',
        },
    },

    '.method-description': {
        fontWeight: 600,
        fontSize: 13,
        lineHeight: '135%',
        textAlign: 'center',
        letterSpacing: '0.01em',
        color: colors.BLUE,
        maxWidth: 220,
        marginBottom: '32.5px',
        width: '100%',
    },

    button: {
        margin: 'auto',
        padding: '9px 13px !important',
        maxWidth: 180,
        width: '100%',
        display: 'block',
    },

    '.method-quiz': {
        backgroundColor: colors.BEIGE_MILESTONE,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        cursor: 'pointer',
        borderRadius: 10,
        marginBottom: 15,
        width: '100%',
        padding: 35,

        [tabletLandscapeUp]: {
            marginRight: 24,
            marginBottom: 0,
        },

        '.method-image': {
            img: {
                maxHeight: 133,
            }
        }
    },

    '.method-box-builder': {
        backgroundColor: colors.BLUE_EXTRA_LIGHT,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        cursor: 'pointer',
        borderRadius: 10,
        width: '100%',
        padding: 35,

        [tabletLandscapeUp]: {
            marginBottom: 0,
        },

        '.method-image': {
            marginBottom: 15,
            img: {
                maxHeight: 108,
                paddingLeft: 20,
            }
        }
    },
});

export default function Picker({ category, hideHeading }: { category: any, hideHeading: boolean }) {
    const history = useHistoryWithReferrer();
    const [showQuiz] = useQuizModal();
    const [showBuilder] = useBuildBundleModal();
    const isSwapping = useIsSwapping();

    return (
        <div className="category picker">
            {!hideHeading &&
                <div className="category-title mb-4">
                    <h2>{category.title}</h2>
                    <div>
                        <p>{category.description}</p>
                    </div>
                </div>
            }
            <div className={style}>
                <Clickable className="method-quiz"
                    onClick={() => {
                        if (!isSwapping) {
                            showQuiz();
                            packAndTrack('Take Survey Clicked', [{ 'view': 'Shop', 'section': 'Shop' }]);
                        } else {
                            history.pushWithReferrer('/swap/survey');
                            packAndTrack('Take Survey Clicked', [{ 'view': 'Swap', 'section': 'Swap' }]);
                        }
                    }}>
                    <div className="method-image">
                        <img
                            loading="lazy"
                            className="desktop"
                            src={imageMethodQuiz}
                            alt={''} />
                    </div>
                    <div>
                        <div className="method-title">
                            Personalized nutrition
                        </div>
                        <div className="method-description">
                            Precise, science-backed nutrition based on your child's age and milestones
                        </div>
                        <button className="button primary blue" aria-label="TAKE THE QUIZ">
                            TAKE THE QUIZ
                        </button>
                    </div>
                </Clickable>
                <Clickable className="method-box-builder"
                    onClick={() => {
                        if (!isSwapping) {
                            showBuilder();
                            packAndTrack('Build Box Clicked', [{ 'view': 'Shop', 'section': 'Shop' }]);
                        } else {
                            history.pushWithReferrer('/swap/builder');
                            packAndTrack('Build Box Clicked', [{ 'view': 'Swap', 'section': 'Swap' }]);
                        }
                    }}>
                    <div className="method-image">
                        <img
                            loading="lazy"
                            className="desktop"
                            src={imageMethodBox}
                            alt={''} />
                    </div>
                    <div>
                        <div className="method-title">
                            Build a Bundle
                        </div>
                        <div className="method-description">
                            Pick and choose the right products <br />for your family's unique food journey.
                        </div>
                        <button className="button primary blue" aria-label="BUILD A BUNDLE">
                            BUILD A BUNDLE
                        </button>
                    </div>
                </Clickable>
            </div>
        </div>
    );
}